import { alpha } from '@mui/material/styles';
import { mobileAppGlassCard } from '../components/layout/mobile/mobileAppTheme';
import { getNativePlatform, isNativeAppShell } from './mobileView';

export const ULTRA_GRID_ACCENT = '#4ce1b6';

const BODY_BG_DARK = '#141820';
const BODY_BG_LIGHT = '#ffffff';

export function getUltraRoomAvatarSize(isMobile = false) {
  return isMobile ? 52 : 62;
}

export function getUltraRoomCardMinHeight(isMobile = false, isWorkspace = false) {
  if (isMobile && isNativeAppShell() && getNativePlatform() === 'android') return 188;
  if (isMobile) return 176;
  if (isWorkspace) return 192;
  return 216;
}

export function getUltraRoomCardSx(isDark, isMobile, colors, options = {}) {
  const { showAsDisabled = false, clickable = false, isWorkspace = false } = options;
  const minHeight = getUltraRoomCardMinHeight(isMobile, isWorkspace);
  const enableHoverLift = clickable && !showAsDisabled && !isWorkspace;
  const isNativeAndroid = isMobile && isNativeAppShell() && getNativePlatform() === 'android';

  const defaultBackground = isDark
    ? `linear-gradient(165deg, ${alpha('#ffffff', 0.04)} 0%, ${BODY_BG_DARK} 42%)`
    : `linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)`;

  const mobileBase = {
    ...mobileAppGlassCard,
    p: 0,
    borderRadius: '18px',
    overflow: 'hidden',
    ...(isDark ? { backgroundColor: BODY_BG_DARK } : {}),
    background: colors.bg || defaultBackground,
    border: isDark ? `1px solid ${alpha(colors.dot, 0.32)}` : `1px solid ${colors.border || alpha(colors.dot, 0.28)}`,
    boxShadow: isDark
      ? `0 10px 24px rgba(0,0,0,0.34), inset 0 1px 0 ${alpha('#ffffff', 0.06)}`
      : colors.shadow || '0 6px 18px rgba(15,23,42,0.08)',
  };

  const desktopBase = {
    p: 0,
    borderRadius: '16px',
    overflow: 'hidden',
    ...(isDark ? { backgroundColor: BODY_BG_DARK } : {}),
    background: colors.bg || defaultBackground,
    border: isDark ? `1px solid ${alpha(colors.dot, 0.3)}` : `1px solid ${colors.border || alpha(colors.dot, 0.26)}`,
    boxShadow: isDark ? '0 6px 18px rgba(0,0,0,0.32)' : (colors.shadow || '0 2px 8px rgba(15,23,42,0.06)'),
  };

  return {
    ...(isMobile ? mobileBase : desktopBase),
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
    height: isNativeAndroid ? '100%' : 'auto',
    minHeight,
    ...(isNativeAndroid
      ? {
          backgroundImage: colors.bg || defaultBackground,
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          boxShadow: `0 8px 18px rgba(0,0,0,0.38), inset 0 1px 0 ${alpha('#ffffff', 0.05)}`,
        }
      : {}),
    opacity: showAsDisabled ? 0.78 : 1,
    cursor: clickable ? 'pointer' : 'default',
    WebkitTapHighlightColor: 'transparent',
    ...(enableHoverLift
      ? {
          transition: 'transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.22s ease',
          '&:hover': {
            transform: isMobile ? 'none' : 'translateY(-4px)',
            boxShadow: isDark
              ? `0 16px 32px rgba(0,0,0,0.42), 0 0 0 1px ${alpha(colors.dot, 0.28)}`
              : '0 10px 26px rgba(15,23,42,0.12)',
            borderColor: alpha(colors.dot, isDark ? 0.45 : 0.38),
          },
          '&:active': {
            transform: isMobile ? 'scale(0.985)' : 'translateY(-2px)',
          },
        }
      : {}),
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      zIndex: 3,
      background: `linear-gradient(90deg, ${alpha(colors.dot, 0.15)} 0%, ${colors.dot} 50%, ${alpha(colors.dot, 0.15)} 100%)`,
    },
  };
}

export function getUltraRoomHeroSx(isMobile, isDark, colors) {
  const avatarSize = getUltraRoomAvatarSize(isMobile);
  const glowStrength = colors.heroGlow ?? (isDark ? 0.12 : 0.08);
  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
    px: isMobile ? 0.85 : 1,
    pt: isMobile ? 0.85 : 1,
    pb: isMobile ? 0.35 : 0.4,
    background: isDark
      ? 'transparent'
      : (colors.heroBg || `radial-gradient(circle at 50% 0%, ${alpha(colors.dot, glowStrength)} 0%, transparent 58%)`),
    ...(isDark
      ? {}
      : {
          '&::after': {
            content: '""',
            position: 'absolute',
            left: '50%',
            top: isMobile ? 28 : 32,
            transform: 'translateX(-50%)',
            width: avatarSize + 12,
            height: avatarSize + 12,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(colors.dot, glowStrength + 0.04)} 0%, transparent 70%)`,
            pointerEvents: 'none',
          },
        }),
  };
}

export function getUltraRoomAvatarSx(isMobile, isDark, colors) {
  const size = getUltraRoomAvatarSize(isMobile);
  return {
    width: size,
    height: size,
    fontSize: isMobile ? '1.2rem' : '1.35rem',
    fontWeight: 800,
    bgcolor: isDark ? alpha(colors.dot, 0.12) : alpha(colors.dot, 0.08),
    color: colors.dot,
    border: `2.5px solid ${alpha(colors.dot, isDark ? 0.72 : 0.55)}`,
    boxShadow: isDark
      ? `0 6px 16px rgba(0,0,0,0.32), 0 0 0 4px ${alpha(colors.dot, 0.08)}`
      : `0 4px 12px rgba(15,23,42,0.1), 0 0 0 4px ${alpha(colors.dot, 0.06)}`,
  };
}

export function getUltraRoomAvatarImageSx() {
  return {
    objectFit: 'cover',
  };
}

export function getUltraRoomStatusChipSx(accentColor, isDark) {
  return {
    position: 'absolute',
    top: 8,
    left: 8,
    zIndex: 2,
    height: 22,
    fontSize: '0.62rem',
    fontWeight: 800,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    bgcolor: isDark ? alpha('#020617', 0.92) : alpha('#ffffff', 0.96),
    color: accentColor,
    border: `1px solid ${alpha(accentColor, 0.45)}`,
    boxShadow: isDark ? `0 4px 14px rgba(0,0,0,0.35)` : `0 4px 12px rgba(15,23,42,0.08)`,
    '& .MuiChip-label': { px: 0.75 },
  };
}

export function getUltraRoomChannelBadgeSx(isMobile = false) {
  return {
    position: 'absolute',
    right: -1,
    bottom: -1,
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: isMobile ? 18 : 20,
    height: isMobile ? 18 : 20,
    borderRadius: '50%',
    bgcolor: 'transparent',
    border: 'none',
    boxShadow: 'none',
    pointerEvents: 'none',
    '& img': {
      width: isMobile ? 16 : 18,
      height: isMobile ? 16 : 18,
      objectFit: 'contain',
      display: 'block',
      filter: 'drop-shadow(0 1px 2px rgba(15, 23, 42, 0.2))',
    },
  };
}

export function getUltraRoomNumberSx(isMobile, isDark, colors) {
  return {
    mt: isMobile ? 0.45 : 0.5,
    color: isDark ? '#f8fafc' : '#0f172a',
    fontWeight: 900,
    fontSize: isMobile ? '1.5rem' : '1.75rem',
    lineHeight: 1,
    letterSpacing: '-0.05em',
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'center',
  };
}

export function getUltraRoomCardBodySx(isDark, isMobile, colors = {}) {
  return {
    px: isMobile ? 0.9 : 1.15,
    pb: isMobile ? 0.85 : 1,
    pt: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
    minWidth: 0,
    bgcolor: colors.bodyBg || 'transparent',
  };
}

export function getUltraRoomCardTextSx(isMobile) {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    gap: isMobile ? 0.35 : 0.45,
  };
}

export function getUltraRoomCardFooterSx(isMobile) {
  return {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 0.45,
    width: '100%',
    mt: 'auto',
    pt: isMobile ? 0.65 : 0.75,
  };
}

export function getUltraRoomGuestNameSx(isDark, colors, muted, isMobile = false) {
  const mutedColor = colors.text
    ? colors.text
    : isDark
      ? '#94a3b8'
      : '#64748b';

  return {
    color: muted
      ? mutedColor
      : isDark
        ? '#f8fafc'
        : '#0f172a',
    fontWeight: muted ? 700 : 700,
    fontSize: isMobile ? '0.78rem' : '0.84rem',
    fontStyle: muted && !colors.text ? 'italic' : 'normal',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    width: '100%',
  };
}

export function getUltraRoomMetaSx(isDark) {
  return {
    color: isDark ? alpha('#cbd5e1', 0.88) : '#64748b',
    fontSize: '0.68rem',
    fontWeight: 600,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    width: '100%',
  };
}

export function getUltraRoomBookingCodeSx(isDark) {
  return {
    color: isDark ? '#94a3b8' : '#475569',
    fontSize: '0.64rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '0.02em',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontVariantNumeric: 'tabular-nums',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
  };
}

export function getUltraRoomBodyChipSx(color, isDark) {
  return {
    height: 22,
    fontSize: '0.6rem',
    fontWeight: 800,
    letterSpacing: '0.03em',
    bgcolor: isDark ? alpha(color, 0.14) : alpha(color, 0.1),
    color: isDark ? color : color,
    border: `1px solid ${alpha(color, isDark ? 0.42 : 0.32)}`,
    boxShadow: isDark ? `inset 0 1px 0 ${alpha('#ffffff', 0.06)}` : 'none',
    '& .MuiChip-label': { px: 0.7 },
  };
}

export function getUltraRoomMenuButtonSx(isDark) {
  return {
    position: 'static',
    color: isDark ? '#e2e8f0' : '#475569',
    bgcolor: isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.05),
    border: `1px solid ${isDark ? alpha('#ffffff', 0.12) : alpha('#000', 0.1)}`,
    width: 28,
    height: 28,
    '&:hover': {
      bgcolor: isDark ? alpha('#ffffff', 0.14) : alpha('#000', 0.08),
    },
  };
}

export function getCleanStatusMeta(cleanStatus) {
  const value = String(cleanStatus || 'clean').toLowerCase();
  if (value === 'dirty') return { label: 'Dirty', color: '#fb923c' };
  if (value === 'inspected') return { label: 'Inspected', color: '#60a5fa' };
  return { label: 'Clean', color: '#4ade80' };
}

export { getUltraRoomStatusPalette } from './roomBoardStatusColors';

// Legacy exports kept for skeleton/other callers — map to new layout where needed
export function getUltraRoomImageWrapSx() {
  return {};
}

export function getUltraRoomImageSx() {
  return {};
}

export function getUltraRoomImageOverlaySx() {
  return { display: 'none' };
}

export function getUltraRoomImagePlaceholderSx() {
  return {};
}

export function getUltraRoomNumberOnImageSx() {
  return {};
}

export function getUltraRoomImageBadgeSx(accentColor, isDark) {
  return getUltraRoomStatusChipSx(accentColor, isDark);
}

const MOBILE_ULTRA_GRID_COLS = 3;

export function getUltraRoomGridContainerSx({
  isDark,
  isMobileLayout = false,
  isWorkspaceLayout = false,
}) {
  if (isMobileLayout) {
    const isNativeAndroid = isNativeAppShell() && getNativePlatform() === 'android';
    const mobileColumnCount = isNativeAndroid ? 2 : MOBILE_ULTRA_GRID_COLS;
    const childSx = {
      minWidth: 0,
      width: isNativeAndroid ? 'calc(50% - 5px)' : '100%',
      flex: isNativeAndroid ? '0 0 calc(50% - 5px)' : undefined,
      height: isNativeAndroid ? '100%' : 'auto',
      minHeight: isNativeAndroid ? getUltraRoomCardMinHeight(true, false) : undefined,
      alignSelf: 'stretch',
    };

    if (isNativeAndroid) {
      return {
        display: 'flex',
        flexWrap: 'wrap',
        minWidth: 0,
        width: '100%',
        gap: '10px',
        p: 1,
        background: 'transparent',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        overflow: 'visible',
        '& > *': childSx,
      };
    }

    return {
      display: 'grid',
      minWidth: 0,
      width: '100%',
      gridTemplateColumns: `repeat(${mobileColumnCount}, minmax(0, 1fr))`,
      gridAutoRows: isNativeAndroid ? `${getUltraRoomCardMinHeight(true, false)}px` : undefined,
      gridAutoFlow: 'row',
      gap: isNativeAndroid ? 0.9 : 0.65,
      p: isNativeAndroid ? 1 : 0.85,
      background: 'transparent',
      alignItems: 'start',
      justifyItems: 'stretch',
      overflow: 'visible',
      contain: 'layout paint',
      '& > *': childSx,
    };
  }

  const minColumnWidth = isWorkspaceLayout ? 172 : 188;
  const gap = isWorkspaceLayout ? 1.1 : 1.75;
  const padding = isWorkspaceLayout ? 1.25 : 2;
  const childSx = {
    minWidth: 0,
    height: 'auto',
    alignSelf: 'start',
  };

  return {
    display: 'grid',
    minWidth: 0,
    gridTemplateColumns: `repeat(auto-fill, minmax(${minColumnWidth}px, 1fr))`,
    gap,
    p: padding,
    alignItems: 'start',
    background:
      isDark || isWorkspaceLayout
        ? 'transparent'
        : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    '& > *': childSx,
  };
}

/** @deprecated Column count is derived in CSS via auto-fill; kept for callers that still read config. */
export function getUltraRoomGridColumnConfig(isMobileLayout, isWorkspaceLayout) {
  if (isMobileLayout) {
    return { minColumnWidth: 100, gapPx: 8, maxColumns: MOBILE_ULTRA_GRID_COLS };
  }
  if (isWorkspaceLayout) {
    return { minColumnWidth: 172, gapPx: 10, maxColumns: 14 };
  }
  return { minColumnWidth: 188, gapPx: 14, maxColumns: 16 };
}
